import { type ColorName, colors } from './colors';

/**
 * ColorNames plus two hardcoded snake case values
 */
export type MappedColorName = ColorName | 'battleship-dark' | 'boulder-dark';
/**
 * All ColorNames as snake case
 */
export type SnakeCaseColorName = CamelToSnakeCase<ColorName>;

// following helpers from:
// https://gist.github.com/kuroski/9a7ae8e5e5c9e22985364d1ddbf3389d?permalink_comment_id=4393122#gistcomment-4393122
type RemoveUnderscoreFirstLetter<S extends string> = S extends `${infer FirstLetter}${infer U}`
  ? `${FirstLetter extends '_' ? U : `${FirstLetter}${U}`}`
  : S;

type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '-' : ''}${RemoveUnderscoreFirstLetter<
      Lowercase<T>
    >}${CamelToSnakeCase<U>}`
  : S;

/** Map color names (and variant names) to their color values */
const realColorMap: Map<MappedColorName | SnakeCaseColorName, string> = new Map();

(Object.entries(colors) as [keyof typeof colors, string][]).forEach(([key, value]) => {
  realColorMap.set(key, value);
  if (key === 'almostBlack') {
    realColorMap.set('battleship-dark', value);
  } else if (key === 'battleship') {
    realColorMap.set('boulder-dark', value);
  }
});

const camelFinder = /[A-Z0-9]/g;

/**
 * Convert camelCaseNames to snake-case names
 */
function convertCamelToSnake(match: string, offset: boolean) {
  const value = match.toLowerCase();
  const hyphen = offset ? '-' : '';
  return hyphen + value;
}

Object.entries(colors).forEach(([name, value]) => {
  const snakeName = name.replace(camelFinder, convertCamelToSnake);
  realColorMap.set(snakeName as ColorName, value as string);
});

/**
 * Convert a color name into it's hex value
 */
export function nameToHex(color: MappedColorName | SnakeCaseColorName) {
  const mappedColor = realColorMap.get(color);
  if (mappedColor) {
    return mappedColor;
  }
  throw new Error(`Color does not exist: ${color}`);
}

export function isColorNameMappedToHex(
  color: string,
): color is MappedColorName | SnakeCaseColorName {
  if (realColorMap.has(color as unknown as ColorName)) {
    return true;
  }
  return false;
}
